import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, sseQuerySchema } from '../schemas';

/**
 * Admin panel. `{ root: true }`, so both paths are spelled absolutely.
 *
 * The three global cache flushes that used to live here —
 * `cleanTagsCache`, `cleanAnchorsCache`, `cleanEquipmentCache` — are DELETED.
 * Each was `requireRole(allowManagementRoles)` and each flushed a cache for
 * EVERY project in the environment (`cache.deleteKeyStartsWith(...Key(''))`,
 * `cache.deleteKey(CacheKeys.staticEquipmentMappings)`), so a Manager of one
 * project could degrade every other one. They had no caller in this repository,
 * none in devops, and none in trusstorutils. Raising them to
 * `allowTrusstorAdminRoles` and declaring them global admin would have been
 * defensible; deleting three routes nobody calls is better. The service methods
 * survive — they are called internally after a bulk write.
 *
 * `cleanAnchorCacheByAreaId` did have a caller (ProjectConfig's floor controller)
 * and moved to `staticAnchors.contract.ts` as
 * `DELETE /projects/:projectId/anchors/cache?areaId=`, because it is an anchors
 * operation and `areaId` is a filter within the project rather than a scope.
 */
export const staticAdminContract = defineContract(
	'staticAdmin',
	{
		getAdminPanelData: route({
			method: 'GET',
			path: '/projects/:projectId/adminPanel',
			params: projectIdParamsSchema,
			response: z.any(),
			operationId: 'getAdminPanelData',
			tags: ['Static Admin'],
			summary: 'Get admin panel data for a project',
		}),
	},
	{ root: true }
);

/**
 * The SSE stream, DELIBERATELY LEFT ON ITS OLD PATH in this commit.
 *
 * Its URL is also a pub/sub CHANNEL KEY: `sse.plugin.ts` registers each client
 * under its sanitized `request.url` and `StreamManagerService` matches publishers
 * to subscribers by string, so moving it is a lockstep change across the route,
 * the publisher in `mergedTags.service.ts`, the frontend's open and
 * `closeStreamIncludes`, and d-orca. A mismatch produces a stream that connects,
 * heartbeats, and then never delivers a frame — no error, no failing test, in
 * either repository. It moves in its own commit, alone.
 *
 * The literal is spelled in full here on purpose: `getAdminPanelData` above no
 * longer starts with `/static/adminPanel`, so the registration-order hack in
 * `staticAdmin.routes.ts` (specific path before parameterized path) has stopped
 * being load-bearing — which is exactly the `{ root: true }` trap, and the reason
 * this route is asserted literally rather than assumed.
 */
export const adminPanelStreamRoute = route({
	method: 'GET',
	path: '/static/adminPanel/stream',
	query: sseQuerySchema.extend({ projectId: requiredStringSchema }),
	response: z.any(),
	operationId: 'adminPanelStream',
	tags: ['Static Admin'],
	summary: 'Stream admin panel data (SSE)',
});
